import { Column, Divider, Row, SizedBox } from "../../../common/components/CommonBaseComponents";
import { C1Text } from "../../../common/components/text/CustomText";
import PrimaryButton from "../../../common/components/button/PrimaryButton";
import SecondaryButton from "../../../common/components/button/SecondaryButton";
import Translate from "../../../common/translate/Translate";
import { Fragment, useEffect, useState } from "react";
import { TitleAndInput } from "../../../common/components/input/TitleAndInput";
import { Theme } from "../../../common/Theme";
import CustomInput from "../../../common/components/input/CustomInput";
import CustomPopup from "../../../common/components/popup/CustomPopup";
import PgCodeService from "../../../service/PgCodeService";
import LocalStorageKey from "../../../common/enums/LocalStorageKey";

// 국가(server)별 PG사 및 pgCodeInfo 입력 필드 구성.
// fields[].key 는 API pgCodeInfo 의 key 와 동일하게 맞춘다.
const PG_CONFIG_BY_COUNTRY = {
    // 태국 - 2C2P
    TH: {
        pgId: "2c2p",
        label: "2C2P",
        fields: [
            { key: "merchantId", title: "Merchant ID" },
            { key: "secretKey", title: "SecretKey" },
        ],
    },
    // 인도 - Pinelabs
    IN: {
        pgId: "pinelab",
        label: "Pinelabs",
        fields: [
            { key: "merchantId", title: "Merchant ID" },
            { key: "accessCode", title: "Access Code" },
            { key: "secureSecret", title: "Secure Secret" },
        ],
    },
    // 한국 - PortOne
    KR: {
        pgId: "portone_kcp",
        label: "PortOne",
        fields: [
            { key: "userCode", title: "User Code" },
            { key: "apiKey", title: "API Key" },
            { key: "apiSecret", title: "API Secret" },
        ],
    },
};

const DEFAULT_PHASE = "OP";

// config.fields 기준으로 빈 값 객체 생성
function buildEmptyValues(config) {
    return config.fields.reduce((acc, field) => {
        acc[field.key] = "";
        return acc;
    }, {});
}

function PGCodeContainer({ storeId }) {
    const pgCodeService = new PgCodeService();

    // 국가별 PG 구성 결정 (미정의 국가는 태국 기준으로 fallback)
    const country = localStorage.getItem(LocalStorageKey.server) || "TH";
    const config = PG_CONFIG_BY_COUNTRY[country] || PG_CONFIG_BY_COUNTRY.TH;

    const [formValues, setFormValues] = useState(() => buildEmptyValues(config));
    const [savedValues, setSavedValues] = useState(() => buildEmptyValues(config));
    const [isEditMode, setIsEditMode] = useState(false);
    // pgId / phase 는 별도 UI 없이 조회값 보존 + 국가 기본값 사용
    const [pgId, setPgId] = useState(config.pgId);
    const [phase, setPhase] = useState(DEFAULT_PHASE);
    const [idPgCode, setIdPgCode] = useState(null);
    const [popupMessage, setPopupMessage] = useState("");

    const setFieldValue = (key, value) => {
        setFormValues((prev) => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        if (!storeId) return;
        pgCodeService
            .getPgCode({ storeId })
            .then((result) => {
                if (result) {
                    const info = result.pgCodeInfo || {};
                    const nextValues = config.fields.reduce((acc, field) => {
                        acc[field.key] = info[field.key] || "";
                        return acc;
                    }, {});
                    setFormValues(nextValues);
                    setSavedValues(nextValues);
                    if (result.pgId) setPgId(result.pgId);
                    if (result.phase) setPhase(result.phase);
                    setIdPgCode(result.idPgCode || null);
                }
            })
            .catch((error) => {
                console.error("Error fetching PG code:", error);
            });
    }, [storeId]);

    const handleSave = () => {
        const hasEmpty = config.fields.some((field) => !(formValues[field.key] || "").trim());
        if (hasEmpty) {
            setPopupMessage("The PG code is invalid.\nPlease check and try again.");
            return;
        }

        const pgCodeInfo = config.fields.reduce((acc, field) => {
            acc[field.key] = formValues[field.key];
            return acc;
        }, {});

        const reqDto = {
            request: {
                store: storeId,
                pgId: pgId,
                pgCodeInfo: pgCodeInfo,
                phase: phase,
                ...(idPgCode ? { idPgCode } : {}),
            },
        };

        const saveFn = idPgCode ? pgCodeService.updatePgCode(reqDto) : pgCodeService.savePgCode(reqDto);
        saveFn
            .then((result) => {
                if (result?.idPgCode) {
                    setIdPgCode(result.idPgCode);
                }
                setSavedValues(formValues);
                setIsEditMode(false);
                setPopupMessage("PG Code saved successfully.");
            })
            .catch((error) => {
                console.error("Error saving PG code:", error);
                setPopupMessage("The PG code is invalid.\nPlease check and try again.");
            });
    };

    const handleCancel = () => {
        setFormValues(savedValues);
        setIsEditMode(false);
    };

    return (
        <SizedBox
            width={"100%"}
            height={"auto"}
            borderRadius={"16px"}
            border={`1px solid ${Theme.color.gray200}`}
            alignItem={"flex-start"}
            justifyContent={"flex-start"}
        >
            <Row
                alignItem={"center"}
                justifyContent={"space-between"}
                padding={"15px 20px"}
                width={"100%"}
                height={"59px"}
            >
                <C1Text>{`PG Code (${config.label})`}</C1Text>
                <CustomInput
                    type={"select"}
                    inputValue={config.label}
                    setInputValue={() => { }}
                    dropdownValues={[{ label: config.label, value: config.label }]}
                    width={"auto"}
                    innerWidth={"50px"}
                    padding={"8px"}
                />
            </Row>
            <Divider />
            <Column width={"100%"} padding={"20px 20px 0 20px"}>
                {config.fields.map((field) => (
                    <Fragment key={field.key}>
                        <TitleAndInput
                            title={field.title}
                            type="normal"
                            placeholder=""
                            inputValue={formValues[field.key] || ""}
                            setInputValue={(v) => setFieldValue(field.key, v)}
                            disabled={!isEditMode}
                        />
                        <Divider marginTop={"11px"} marginBottom={"11px"} lineStyle={"custom-dotted"} />
                    </Fragment>
                ))}
            </Column>
            {isEditMode && (
                <Row width={"100%"} padding={"16px 20px"} justifyContent={"flex-end"}>
                    <SizedBox width={"10px"} />
                    <SecondaryButton text={"Cancel"} onClick={handleCancel} />
                    <SizedBox width={"10px"} />
                    <PrimaryButton
                        icon={"checked_white"}
                        text={Translate("common.save")}
                        onClick={handleSave}
                    />
                </Row>
            )}
            <CustomPopup
                isOpen={!!popupMessage}
                onConfirm={() => setPopupMessage("")}
                message={popupMessage
                    .split("\n")
                    .map((line, index) => (index === 0 ? line : [<br key={index} />, line]))}
            />
        </SizedBox>
    );
}

export default PGCodeContainer;
